import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReturnButtons from '../../components/core/ReturnButtons';
import ActivityIndicator from '../../components/core/ActivityIndicator.jsx';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import ee from '../../../services/earth-engine';
import { Box, Grid, Grow, Card, CardContent, Avatar, Paper, IconButton, Collapse } from '@material-ui/core';
import { Waves, ShowChart, GetApp, ExpandLess, ExpandMore, LayersOutlined } from '@material-ui/icons';
import GoogleMap from '../../components/map/GoogleMap';
import BackgroundMapPanel from '../../components/map/BackgroundMapPanel';
import OverlayPanelsToggle from '../../components/map/OverlayPanelsToggle';
import ImageChooserCard from '../../components/map/ImageChooserCard';
import ActionsCard from '../../components/intertidal/ActionsCard';
import { ShapeList } from '../../components/map/ShapeList';
import LoadedImagesAccordion from '../../components/map/LoadedImagesAccordion';
import { Actions as Map } from '../../../store/ducks/map';
import { Actions as Snack } from '../../../store/ducks/snacks';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import TourGuider from '../../components/tour/TourGuider';
import { useGuidedTour, useLocalStorage } from '../../../common/utils';
import { Button } from '@material-ui/core';
import { OverlayHelper, INDEX_OPTIONS } from '../../components/intertidal/IntertidalZoneDefiner';
import Export from '../../../services/export';
import { INTERNALS } from '../../../common/metadata';
import { omit } from 'lodash';
import { CircularProgress, Typography } from '@material-ui/core';
import KrigingPanel from '../../components/intertidal/KrigingPanel';
import TideChartModal from '../../components/intertidal/TideChartModal';
import { exportSession } from '../../../common/utils.js';
import { exportGeoJson } from '../../../algorithms/intertidal/geoJsonExport';
import { insertByIndex, runWaterlines } from '../../../algorithms/intertidal/waterlineRunner';
import { useRegisterGeoJsonExport, useRegisterPdfExport, useRegisterSessionExport, useThemeMode } from '../../contexts/index.jsx';
import {
  buildMapStyles,
  createShorelineTheme,
  OVERLAY_COLUMN_MAX_HEIGHT,
  OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
  OVERLAY_SIDE_GUTTER,
  OVERLAY_SCROLLBAR_GUTTER,
  OVERLAY_COLUMN_PADDING_TOP,
  OVERLAY_MAIN_COLUMN_WIDTH,
  OVERLAY_SIDE_COLUMN_WIDTH,
  OVERLAY_PANEL_WIDTH,

  OVERLAY_COLUMN_LAYOUT,
  fitBoundsWithOverlayPadding,
  fitCoordinatesWithOverlayPadding,
} from '../Shoreline/theme.js';
import { buildSessionExport, readPendingIntertidalSession } from '../../../algorithms/intertidal/sessionExport.js';
import { buildIntertidalReport, REPORT_MODULE } from '../../../algorithms/intertidal/pdfReport';
import { captureImageForReport, renderReport, reportFileName } from '../../../common/pdfReport';
import { APP_ROOT_SELECTOR, settleMapForCapture } from '../../../common/captureElement';
import { aoiGeometry, aoiPaths } from '../../../common/aoi';

const samplingInterval = 10;
const landSatThreshold = 20;
const sentinelThreshold = 5;
const landsatScale = 30;
const sentinelScale = 10;

/**
 * @function isLandsatMission
 * @description Checks whether the given mission is a Landsat mission.
 * @param {Object} mission - Mission configuration object.
 * **Called by:
 * @see {getMinVerticesThreshold}
 * @see {getScaleForMission}
 * @memberof module:Intertidal
 * @returns {boolean} True if the mission is Landsat.
 */
const isLandsatMission = (mission) => {
  if (!mission?.name) return false;
  return mission.name.indexOf('LANDSAT') !== -1;
};

/**
 * @function getMinVerticesThreshold
 * @description Resolves the minimum polygon vertex count required for a valid waterline,
 * based on the currently selected mission (20 for Landsat, 5 for Sentinel).
 * @see {@link isLandsatMission}
 * **Called by:
 * @see {ProcessingIntertidalPage} - via calculateWaterlineForImage.
 * @memberof module:Intertidal
 * @returns {Number} Minimum vertex count.
 */
const getMinVerticesThreshold = () => {
  const mission = JSON.parse(window.sessionStorage.getItem('selectedMission'));
  if (isLandsatMission(mission)) {
    return landSatThreshold;
  }
  return sentinelThreshold;
};

/**
 * @function getScaleForMission
 * @description Resolves the vectorization scale, in meters, based on the currently selected
 * mission (30m for Landsat, 10m for Sentinel).
 * @see {@link isLandsatMission}
 * **Called by:
 * @see {ProcessingIntertidalPage} - via calculateWaterlineForImage.
 * @memberof module:Intertidal
 * @returns {Number} Vectorization scale, in meters.
 */
const getScaleForMission = () => {
  const mission = JSON.parse(window.sessionStorage.getItem('selectedMission'));
  if (isLandsatMission(mission)) {
    return landsatScale;
  }
  return sentinelScale;
};

/**
 * @function convertGeoJSONToLineStrings
 * @description Normalizes a GeoJSON FeatureCollection's features into LineStrings: converts
 * LinearRing/Polygon/MultiPolygon rings (dropping their closing point) and MultiLineString parts
 * into individual LineString features; LineString features pass through unchanged; any other
 * geometry type is dropped.
 * @param {Object} evaluated - GeoJSON FeatureCollection to normalize.
 * **Called by:
 * @see {ProcessingIntertidalPage} - via startWaterlineRun and printWaterlines.
 * @see {fetchAndPlotSingleWaterline}
 * @memberof module:Intertidal
 * @returns {Object} The same FeatureCollection, with its `features` replaced by LineString features.
 */
function convertGeoJSONToLineStrings(evaluated) {
  if (!evaluated || evaluated.type !== 'FeatureCollection' || !Array.isArray(evaluated.features)) {
    throw new Error('O objeto fornecido deve ser um FeatureCollection GeoJSON.');
  }
  const newFeatures = [];
  evaluated.features.forEach((feature) => {
    if (!feature || feature.type !== 'Feature') return;
    const geom = feature.geometry;
    if (!geom) return;
    const props = feature.properties || {};
    const type = geom.type;
    const coords = geom.coordinates;
    const removeClosingPoint = (ring) => {
      if (ring.length >= 2) {
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (
          Array.isArray(first) &&
          Array.isArray(last) &&
          first.length === last.length &&
          first.every((val, i) => val === last[i])
        ) {
          return ring.slice(0, -1);
        }
      }
      return ring;
    };
    if (type === 'LinearRing') {
      if (Array.isArray(coords)) {
        const line = removeClosingPoint(coords);
        if (line.length >= 2) {
          newFeatures.push({
            type: 'Feature',
            properties: props,
            geometry: { type: 'LineString', coordinates: line },
          });
        }
      }
    } else if (type === 'Polygon') {
      if (Array.isArray(coords) && coords.length > 0 && Array.isArray(coords[0])) {
        const outerRing = removeClosingPoint(coords[0]);
        if (outerRing.length >= 2) {
          newFeatures.push({
            type: 'Feature',
            properties: props,
            geometry: { type: 'LineString', coordinates: outerRing },
          });
        }
      }
    } else if (type === 'MultiPolygon') {
      if (Array.isArray(coords)) {
        coords.forEach((polygon) => {
          if (Array.isArray(polygon) && polygon.length > 0 && Array.isArray(polygon[0])) {
            const outerRing = removeClosingPoint(polygon[0]);
            if (outerRing.length >= 2) {
              newFeatures.push({
                type: 'Feature',
                properties: props,
                geometry: { type: 'LineString', coordinates: outerRing },
              });
            }
          }
        });
      }
    } else if (type === 'MultiLineString') {
      if (Array.isArray(coords)) {
        coords.forEach((line) => {
          if (Array.isArray(line) && line.length >= 2) {
            newFeatures.push({
              type: 'Feature',
              properties: props,
              geometry: { type: 'LineString', coordinates: line },
            });
          }
        });
      }
    } else if (type === 'LineString') {
      if (Array.isArray(coords) && coords.length >= 2) {
        newFeatures.push({
          type: 'Feature',
          properties: props,
          geometry: { type: 'LineString', coordinates: coords },
        });
      }
    }
  });
  evaluated.features = newFeatures;
  return evaluated;
}

/**
 * @function prepareGeoJsonForGoogleMaps
 * @description Rewraps a sanitized FeatureCollection into a plain GeoJSON FeatureCollection of
 * LineString features suitable for plotting on Google Maps.
 * @param {Object} sanitizedEvaluated - FeatureCollection already normalized to LineStrings.
 * **Called by:
 * @see {ProcessingIntertidalPage} - via printWaterlines and fetchAndPlotSingleWaterline.
 * @memberof module:Intertidal
 * @returns {Object|null} GeoJSON FeatureCollection ready for the map, or null if the input is empty/invalid.
 */
const prepareGeoJsonForGoogleMaps = (sanitizedEvaluated) => {
  if (!sanitizedEvaluated || !sanitizedEvaluated.features || sanitizedEvaluated.features.length === 0) {
    console.warn('SanitizedEvaluated inválido ou vazio.');
    return null;
  }
  return {
    type: 'FeatureCollection',
    features: sanitizedEvaluated.features.map((feature) => ({
      type: 'Feature',
      properties: feature.properties || {},
      geometry: {
        type: 'LineString',
        coordinates: feature.geometry.coordinates,
      },
    })),
  };
};

let evaluated = null;
let evaluatedEE = null;

export class WaterLinesExport {
  /**
   * @function exportShapeFile
   * @description Exports the currently plotted waterlines (`evaluatedEE`) as a shapefile group,
   * stripping the internal `INTERNALS` metadata property from each feature first.
   * @see {@link Export.table.toDevice.asShapefileGroup}
   * **Called by:
   * @see {ActionList} - via the "Export data" list item's onClick.
   * @see {ProcessingIntertidalPage} - via its own "Export data" button's onClick.
   * @memberof module:Intertidal
   * @returns {void}
   */
  static exportShapeFile = function () {
    const featuresSanitized = evaluatedEE.features.map((f) => ({
      ...f,
      properties: omit(f.properties || {}, [INTERNALS]),
    }));

    Export.table.toDevice.asShapefileGroup(
      [{ features: featuresSanitized }],
      Export.defaultOptions.device.shapefileGroup('output', 'vectors'),
    );
  };
}

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles.
 * Defines CSS-in-JS styles for the intertidal processing page components.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Intertidal
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexFlow: 'column',
    ...OVERLAY_COLUMN_LAYOUT,
  },
  panelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexShrink: 0,
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mapOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '100%',
    zIndex: 10,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
  },
  margin: {
    margin: '5px',
  },
  right: {
    textAlign: 'right',
  },
}));

/**
 * @function thresholdingAlgorithm
 * @description Computes Otsu's dynamic threshold from an NDWI histogram: for each possible split
 * point, computes the between-class variance of the two resulting groups, and returns the mean
 * value that maximizes it.
 * @param {ee.Dictionary} histogramData - Histogram dictionary with `histogram` (counts) and `bucketMeans` keys.
 * **Called by:
 * @see {calculateWaterlineForImage}
 * @memberof module:Intertidal
 * @returns {ee.Number|null} The Otsu threshold value, or null if no histogram data was given.
 */
var thresholdingAlgorithm = function (histogramData) {
  if (!histogramData) {
    console.error('thresholdingAlgorithm recebeu um histograma nulo.');
    return null;
  }
  var counts = ee.Array(ee.Dictionary(histogramData).get('histogram'));
  var means = ee.Array(ee.Dictionary(histogramData).get('bucketMeans'));
  var size = means.length().get([0]);
  var total = counts.reduce(ee.Reducer.sum(), [0]).get([0]);
  var sum = means.multiply(counts).reduce(ee.Reducer.sum(), [0]).get([0]);
  var mean = sum.divide(total);
  var indices = ee.List.sequence(1, size);

  var bss = indices.map(function (i) {
    var aCounts = counts.slice(0, 0, i);
    var aCount = aCounts.reduce(ee.Reducer.sum(), [0]).get([0]);
    var aMeans = means.slice(0, 0, i);
    var aMean = aMeans.multiply(aCounts).reduce(ee.Reducer.sum(), [0]).get([0]).divide(aCount);
    var bCount = total.subtract(aCount);
    var bMean = sum.subtract(aCount.multiply(aMean)).divide(bCount);
    return aCount.multiply(aMean.subtract(mean).pow(2)).add(bCount.multiply(bMean.subtract(mean).pow(2)));
  });

  return means.sort(bss).get([-1]);
};

/**
 * @function ProcessingIntertidalPage
 * @description Intertidal component for exhibiting satellite images, map visualization,
 * automatic waterline identification and tide-level assignment, AWEI/Kriging overlay
 * management, and waterline shapefile export.
 * @see {@link GoogleMap} - Google Map component
 * @see {@link TourGuider} - Interface guided tour component
 * @see {@link calculateWaterlineForImage}
 * @see {@link startWaterlineRun}
 * **Called by:
 * @see {MainPage} - via the `/intertidal/result` Route (imported as ProcessingIntertidalPage).
 * @memberof module:Intertidal
 * @returns {JSX.Element} Complete processing page JSX element
 */
const ProcessingIntertidalPage = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const ROI = useRef();
  const boundsRef = useRef(null);
  const mapRef = useRef();
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const zoneOnlyMode = window.localStorage.getItem('intertidalZoneOnly') === 'true';
  const [currentImage, setCurrentImage] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const coordinates = useSelector((state) => state.acquisition.coordinates);
  /**
   * The name of the KML the area came from, when it came from one. The export is named after it, so
   * a folder of saved analyses says which stretch of coast each one is.
   */
  const kmlFileName = useSelector((state) => state.acquisition.kmlFileName);
  const availableDates = useSelector((state) => state.acquisition.availableDates);
  const colors = [
    '#FF0000',
    '#00FF00',
    '#0000FF',
    '#FFFF00',
    '#00FFFF',
    '#FF00FF',
    '#FFA500',
    '#800080',
    '#008080',
    '#000000',
  ];

  const [waterlineLayers, setWaterlineLayers] = useState([]);
  const [tideChartOpen, setTideChartOpen] = useState(false);
  const [aweiOverlays, setAweiOverlays] = useState([]);
  const [aweiVisible, setAweiVisible] = useState(true);
  const [aweiThreshold, setAweiThreshold] = useState(0);
  const [customLayers, setCustomLayers] = useState([]);

  const [layersPanelOpen, setLayersPanelOpen] = useLocalStorage('intertidalLayersPanelOpen', true);
  const [mapType, setMapType] = useLocalStorage('intertidalMapType', 'roadmap');
  const [labelsVisible, setLabelsVisible] = useLocalStorage('intertidalMapLabels', true);
  const outerTheme = useTheme();
  const { shorelineThemeMode } = useThemeMode();
  const intertidalTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [outerTheme, shorelineThemeMode],
  );

  const handleMapTypeChange = (type) => {
    setMapType(type);
    mapRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ styles: buildMapStyles({ themeMode: shorelineThemeMode, mapType, labelsVisible }) });
  }, [shorelineThemeMode, mapType, labelsVisible]);

  const [isKrigingProcessing, setIsKrigingProcessing] = useState(false);

  const [krigingStep, setKrigingStep] = useState(null);
  const [removedPolylineIndices, setRemovedPolylineIndices] = useState(new Set());
  const [selectedDates, setSelectedDates] = useState(new Set());
  const isRemovalModeRef = useRef(false);
  const removalListenersRef = useRef([]);

  const [krigingOverlay, setKrigingOverlay] = useState(null);
  const [krigingVisible, setKrigingVisible] = useState(true);
  const [krigingMinValue, setKrigingMinValue] = useState(null);
  const [krigingMaxValue, setKrigingMaxValue] = useState(null);
  const [krigingRealMinValue, setKrigingRealMinValue] = useState(null);
  const [krigingRealMaxValue, setKrigingRealMaxValue] = useState(null);
  const [krigingExportData, setKrigingExportData] = useState(null);
  const [isExportingKrigingTiff, setIsExportingKrigingTiff] = useState(false);

  useEffect(() => {
    if (!map) return;
    const mapClickListener = window.google.maps.event.addListener(map, 'click', () => {
      if (selectedPolyline) {
        selectedPolyline.setOptions({
          strokeWeight: 2,
          strokeColor: selectedPolyline.originalColor,
        });
      }
      if (selectedInfoWindow) selectedInfoWindow.close();
      selectedPolyline = null;
      selectedInfoWindow = null;
    });
    window.google.maps.event.removeListener(mapClickListener);
  }, [map]);

  useEffect(() => {
    if (!(mapReady && map)) return;
    const coordinatesArray = JSON.parse(window.localStorage.getItem('AOIIntertidal'));
    if (coordinatesArray && coordinatesArray.length > 0) dispatch(Map.centralizeMap(coordinatesArray));
    setTimeout(() => {}, 500);
  }, [mapReady, map]);

  const roiHighlighted = useRef(false);

  /**
   * @function highlightROI
   * @description Emphasizes or restores the ROI outline's stroke weight/opacity. Not currently
   * called anywhere in the codebase.
   * @param {boolean} on - Whether to highlight (thicker, opaque) or restore (thinner, translucent) the ROI.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const highlightROI = (on) => {
    if (!ROI.current) return;
    ROI.current.setOptions({
      strokeWeight: on ? 8 : 5,
      strokeOpacity: on ? 1.0 : 0.6,
    });
  };

  /**
   * @function calculateWaterlineForImage
   * @description Derives the smoothed, tide-tagged waterline for a single image.
   * 1. Skips processing (returns an empty collection) if the image has no valid NDWI pixels.
   * 2. Computes the NDWI histogram and its Otsu threshold, adjusts it by the pixel standard
   *    deviation, then vectorizes the water/land boundary at that adjusted threshold, keeping
   *    only polygons above the mission-appropriate minimum vertex count.
   * 3. For each valid polygon, intersects it with a slightly-eroded AOI, smooths it with a
   *    Gaussian filter, and tags it with the image's datetime and the closest tide gauge reading.
   * @param {ee.Image} image - Image with an 'ndwi' band.
   * @param {ee.Geometry} geometry - Area of interest geometry.
   * @param {Number} scale - Vectorization scale, in meters.
   * @param {ee.FeatureCollection} tideGauge - Tide gauge readings to match against.
   * @see {@link thresholdingAlgorithm}
   * @see {@link getMinVerticesThreshold}
   * @see {@link getScaleForMission}
   * @see {@link linearGaussianFilter}
   * @see {@link getClosestFeatureImageAndDado}
   * **Called by:
   * @see {startWaterlineRun}
   * @memberof module:Intertidal
   * @returns {ee.FeatureCollection} Smoothed, tide-tagged waterline features (empty if none found).
   */
  const calculateWaterlineForImage = (image, geometry, scale, tideGauge) => {
    // Selects whichever index the user picked in step 4 (defaults to 'ndwi', matching the
    // previous hardcoded behavior), then renames it back to the fixed 'ndwi' label so every
    // other reference in this function (histogram/stdDev/count `.get('ndwi')` calls below) keeps
    // working unchanged regardless of which of the 4 indices is actually selected.
    const ndwi = image.select(OverlayHelper.waterlineIndex || 'ndwi').rename('ndwi');
    const missionScale = getScaleForMission();
    const bandCount = ndwi
      .reduceRegion({
        reducer: ee.Reducer.count(),
        geometry: geometry,
        scale: missionScale,
        maxPixels: 1e12,
      })
      .get('ndwi');
    /**
     * @function process
     * @description Computes the vectorized, minimum-vertex-filtered waterline candidates for the
     * outer `image`, given it has valid NDWI pixels.
     * **Called by:
     * @see {calculateWaterlineForImage}
     * @memberof module:Intertidal
     * @returns {ee.FeatureCollection|ee.Algorithms.If} Smoothed waterline features, via processAllValidFeatures.
     */
    const process = () => {
      const histogram = ndwi.reduceRegion({
        reducer: ee.Reducer.histogram(),
        geometry: geometry,
        scale: missionScale,
      });
      const baseThreshold = thresholdingAlgorithm(histogram.get('ndwi'));
      const stdDevResult = ndwi.reduceRegion({
        reducer: ee.Reducer.stdDev(),
        geometry: geometry,
        scale: missionScale,
        maxPixels: 1e9,
      });
      const stdDevValue = ee.Number(stdDevResult.get('ndwi'));
      const THadjustment = stdDevValue.add(1);
      const adjustedThreshold = ee.Number(baseThreshold).subtract(THadjustment.multiply(ee.Number(baseThreshold)));

      const waterLandVectors = ndwi
        .clip(geometry)
        .lte(adjustedThreshold)
        .reduceToVectors({
          scale: scale,
          maxPixels: 1e12,
          eightConnected: false,
        })
        .filter(ee.Filter.eq('label', 1));
      const cleanedFeatures = waterLandVectors
        .map((feature) => {
          const numVertices = ee.List(feature.geometry().coordinates().get(0)).size();
          const shoreline = ee.Geometry.MultiLineString(feature.geometry().coordinates().get(0)).intersection(
            geometry.buffer(-10),
          );

          return ee.Feature(shoreline).set('numVertices', numVertices);
        })
        .filter(ee.Filter.gt('numVertices', getMinVerticesThreshold()));
      cleanedFeatures.evaluate((featureCollection) => {
        featureCollection.features.forEach((feature) => {
          const coords = feature.geometry.coordinates;
          let path;
          if (feature.geometry.type === 'LineString') {
            path = coords.map((coord) => ({
              lat: coord[1],
              lng: coord[0],
            }));
          } else if (feature.geometry.type === 'MultiLineString') {
            path = coords[0].map((coord) => ({
              lat: coord[1],
              lng: coord[0],
            }));
          }
        });
      });
      return ee.Algorithms.If(
        cleanedFeatures.size().gt(0),
        processAllValidFeatures(cleanedFeatures, ndwi),
        ee.FeatureCollection([]),
      );
    };

    /**
     * @function processAllValidFeatures
     * @description Smooths each valid waterline feature and tags it with the image's datetime
     * and the closest tide gauge reading.
     * @param {ee.FeatureCollection} featureCollection - Valid (minimum-vertex) waterline candidates.
     * @param {ee.Image} img - Source image, with a `datetime` property.
     * @see {@link getClosestFeatureImageAndDado}
     * @see {@link getCoordinatesFromGeometry}
     * @see {@link linearGaussianFilter}
     * **Called by:
     * @see {process}
     * @memberof module:Intertidal
     * @returns {ee.FeatureCollection} Smoothed, tide-tagged waterline features.
     */
    const processAllValidFeatures = (featureCollection, img) => {
      const tideResult = getClosestFeatureImageAndDado(img, tideGauge);
      const datetime = ee.Date(img.get('datetime')).format();
      const dtGaugeFormated = ee.Date(tideResult.dateGauge).format('yyyy-MM-dd HH:mm');
      const dtImageFormated = ee.Date(tideResult.dateImage).format('yyyy-MM-dd HH:mm');

      /**
       * @function getCoordinatesFromGeometry
       * @description Extracts a flat coordinate list from a geometry, unwrapping the outer ring
       * if it's a MultiLineString.
       * @param {ee.Geometry} geometry - LineString or MultiLineString geometry.
       * **Called by:
       * @see {processAllValidFeatures}
       * @memberof module:Intertidal
       * @returns {ee.List} Flat list of [lng, lat] coordinates.
       */
      const getCoordinatesFromGeometry = (geometry) => {
        const geomType = geometry.type();
        return ee.Algorithms.If(
          geomType.equals('MultiLineString'),
          ee.List(geometry.coordinates().get(0)),
          geometry.coordinates(),
        );
      };

      const allWaterlines = featureCollection.map((feature) => {
        const line = feature.geometry();
        const coords = getCoordinatesFromGeometry(line);
        const smoothedCoords = linearGaussianFilter(coords);
        return ee.Feature(ee.Geometry.LineString(smoothedCoords)).set({
          dateTime: datetime,
          tideValue: tideResult.tideValue,
          dateGauge: dtGaugeFormated,
          dateImage: dtImageFormated,
        });
      });

      return allWaterlines;
    };

    return ee.FeatureCollection(ee.Algorithms.If(ee.Number(bandCount).gt(0), process(), ee.FeatureCollection([])));
  };

  /**
   * @function startWaterlineRun
   * @description Asks Earth Engine for the waterline of every selected image, a few at a time,
   * drawing each one as its result lands and registering its layer (date/color/tide/polylines) for
   * the layer list.
   * Results arrive out of order, so each layer records where its own features landed in
   * `evaluatedEE` and is placed in the list by image index - the list reads in image order however
   * the answers came back.
   * @param {ee.ImageCollection} imageCollection - Full image collection being processed.
   * @param {Number} numImages - Total number of images in the collection.
   * @param {ee.Geometry} geometry - Area of interest geometry.
   * @param {Number} scale - Vectorization scale, in meters.
   * @see {@link runWaterlines} - why several run at once rather than one after another.
   * @see {@link insertByIndex}
   * @see {@link calculateWaterlineForImage}
   * @see {@link convertGeoJSONToLineStrings}
   * @see {@link plotEvaluatedSync}
   * **Called by:
   * @see {ProcessingIntertidalPage} - from the main processing effect.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const startWaterlineRun = (imageCollection, numImages, geometry, scale) => {
    const images = imageCollection.toList(numImages);
    setCurrentImage(0);
    runWaterlines({
      total: numImages,
      request: (index) => calculateWaterlineForImage(ee.Image(images.get(index)), geometry, scale, tideGauge),
      onResult: (index, waterlineGeoJSON, error) => {
        if (error) {
          console.error(`Erro ao processar imagem ${index + 1}:`, error);
          return;
        }
        if (!waterlineGeoJSON?.features?.length) {
          console.log(`Nenhuma linha válida encontrada para a imagem ${index + 1}.`);
          return;
        }
        const colorForThisImage = colors[index % colors.length];
        const sanitized = convertGeoJSONToLineStrings(waterlineGeoJSON);
        const result = plotEvaluatedSync(map, sanitized, colorForThisImage);
        if (!evaluatedEE) evaluatedEE = { type: 'FeatureCollection', features: [] };
        const featureStartIndex = evaluatedEE.features.length;
        evaluatedEE.features.push(...sanitized.features);
        if (result?.polylines?.length > 0) {
          setWaterlineLayers((prev) =>
            insertByIndex(prev, {
              imageIndex: index,
              date: result.dateTime,
              color: colorForThisImage,
              tideValue: result.tideValue,
              polylines: result.polylines,
              visible: true,
              featureStartIndex,
              featureCount: sanitized.features.length,
            }),
          );
        }
      },
      onProgress: ({ completed }) => setCurrentImage(completed),
      onDone: () => {
        console.log('Processamento de todas as imagens concluído.');
        setIsProcessing(false);
      },
    });
  };

  useEffect(() => {
    if (!(mapReady && map)) return;

    const pendingIntertidal = readPendingIntertidalSession();
    if (pendingIntertidal) {
      const coordinatesArray = JSON.parse(window.localStorage.getItem('AOIIntertidal'));
      if (coordinatesArray && coordinatesArray.length > 0) {
        dispatch(Map.centralizeMap(coordinatesArray));
      }

      evaluatedEE = { type: 'FeatureCollection', features: pendingIntertidal.waterlineFeatures || [] };

      (pendingIntertidal.waterlineLayerMeta || []).forEach((meta) => {
        const layerFeatures = evaluatedEE.features.slice(meta.featureStartIndex, meta.featureStartIndex + meta.featureCount);
        const result = plotEvaluatedSync(map, { type: 'FeatureCollection', features: layerFeatures }, meta.color);

        if (meta.visible === false) {
          result.polylines.forEach((polyline) => polyline.setMap(null));
        }

        setWaterlineLayers((prev) => [
          ...prev,
          {
            date: meta.date,
            color: meta.color,
            tideValue: meta.tideValue,
            polylines: result.polylines,
            visible: meta.visible !== false,
            featureStartIndex: meta.featureStartIndex,
            featureCount: meta.featureCount,
          },
        ]);
      });

      return;
    }

    setTimeout(() => {
      setIsLoading(true);
    }, 100);

    const coordinatesArray = JSON.parse(window.localStorage.getItem('AOIIntertidal'));

    if (coordinatesArray && coordinatesArray.length > 0) {
      dispatch(Map.centralizeMap(coordinatesArray));
    }

    const geometry = aoiGeometry(coordinatesArray);
    const assetList = ee.List(OverlayHelper.imgsToStep5);
    const imageCollection = ee.ImageCollection(assetList);
    const imageCollectionWithDatetime = imageCollection.map(addDatetimeProperty);

    if (OverlayHelper.aweiLayerURLs && OverlayHelper.aweiLayerURLs.length > 0) {
      const formatedCoordinates = coordinatesArray.map((coord) => ({
        lat: coord[1],
        lng: coord[0],
      }));
      const bounds = new window.google.maps.LatLngBounds();
      formatedCoordinates.forEach((coord) => bounds.extend(coord));

      const overlays = drawAweiLayers(map, OverlayHelper.aweiLayerURLs, bounds);
      setAweiOverlays(overlays);
      setAweiThreshold(OverlayHelper.aweiThreshold || 0);
      console.log(`${overlays.length} camadas AWEI adicionadas.`);
    } else {
      console.warn('Nenhuma camada AWEI disponível em OverlayHelper.aweiLayerURLs');
      console.warn('OverlayHelper completo:', OverlayHelper);
    }

    if (zoneOnlyMode) {
      console.log('Modo "apenas Produto 1" ativo — pulando extração de waterlines e Kriging.');
    } else {
      imageCollectionWithDatetime.size().evaluate((numImages, error) => {
        if (error) {
          console.error('Erro ao obter o tamanho da coleção:', error);
          return;
        }
        if (numImages > 0) {
          setTotalImages(numImages);
          setIsProcessing(true);
          startWaterlineRun(imageCollectionWithDatetime, numImages, geometry, getScaleForMission());
        } else {
          console.log('Nenhuma imagem para processar.');
        }
      });
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [mapReady, map]);

  useEffect(() => {
    if (aweiOverlays.length > 0) {
      setCustomLayers((prev) => {
        const index = prev.findIndex((l) => l.id === 'awei-raster');
        const newLayer = {
          id: 'awei-raster',
          name:
            t('forms.intertidal.aweiZone', {
              index: t(
                `forms.intertidal.${
                  INDEX_OPTIONS.find((option) => option.value === OverlayHelper.zoneIndex)?.labelKey || 'indexAwei'
                }`,
              ),
            }) || 'Zona Intertidal AWEI',
          type: 'ground-overlay',
          overlays: aweiOverlays,
          visible: aweiVisible,
          metadata: { threshold: aweiThreshold },
          icon: (
            <Box
              style={{
                width: 30,
                height: 15,
                background: 'linear-gradient(to right, white, blue)',
                border: '1px solid #666',
                borderRadius: 2,
              }}
            />
          ),
        };

        let copy = [...prev];
        if (index >= 0) {
          copy[index] = newLayer;
        } else {
          copy.push(newLayer);
        }
        return copy;
      });
    }
  }, [aweiOverlays, aweiThreshold, aweiVisible, t]);

  useEffect(() => {
    if (waterlineLayers.length > 0) {
      setCustomLayers((prev) => {
        const filtered = prev.filter((l) => l.id !== 'waterlines');
        return [
          ...filtered,
          {
            id: 'waterlines',
            name: t('forms.intertidal.waterlines') || 'Waterlines',
            type: 'group',
            children: waterlineLayers.map((layer, index) => ({
              id: `waterline-${index}`,
              name: layer.date
                ? `${new Date(layer.date).toLocaleDateString()} | ${layer.tideValue?.toFixed(2)}m`
                : `${t('forms.intertidal.layer') || 'Layer'} ${index + 1}`,
              type: 'polyline',
              overlays: layer.polylines,
              visible: layer.visible,
              color: layer.color,
            })),
          },
        ];
      });
    }
  }, [waterlineLayers, t]);

  useEffect(() => {
    if (krigingOverlay) {
      setCustomLayers((prev) => {
        const filtered = prev.filter((l) => l.id !== 'kriging-interpolation');
        return [
          ...filtered,
          {
            id: 'kriging-interpolation',
            name: t('forms.intertidal.krigingInterpolation') || 'Interpolação Kriging',
            type: 'ground-overlay',
            overlays: [krigingOverlay],
            visible: krigingVisible,
            metadata: {
              samplingInterval,
            },
            icon: (
              <Box
                style={{
                  width: 30,
                  height: 15,
                  background: 'linear-gradient(to right, blue, cyan, yellow, orange, red)',
                  border: '1px solid #666',
                  borderRadius: 2,
                }}
              />
            ),
          },
        ];
      });
    }
  }, [krigingOverlay, krigingVisible, samplingInterval, t]);

  useEffect(() => {
    if (krigingStep === 1) {
      isRemovalModeRef.current = true;

      const listeners = [];
      waterlineLayers.forEach((layer, layerIdx) => {
        layer.polylines.forEach((polyline, polyIdx) => {
          const key = `${layerIdx}-${polyIdx}`;
          const listener = polyline.addListener('click', () => {
            setRemovedPolylineIndices((prev) => {
              const next = new Set(prev);
              if (next.has(key)) {
                next.delete(key);
                polyline.setOptions({
                  strokeOpacity: 1.0,
                  strokeColor: polyline.originalColor,
                  strokeWeight: 2,
                  icons: [],
                });
              } else {
                next.add(key);
                polyline.setOptions({
                  strokeOpacity: 0.3,
                  strokeColor: '#999999',
                  strokeWeight: 2,
                  icons: [
                    {
                      icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 2 },
                      offset: '0',
                      repeat: '10px',
                    },
                  ],
                });
              }
              return next;
            });
          });
          listeners.push({ polyline, listener });
        });
      });
      removalListenersRef.current = listeners;

      return () => {
        isRemovalModeRef.current = false;
        listeners.forEach(({ listener }) => {
          window.google.maps.event.removeListener(listener);
        });
        removalListenersRef.current = [];
      };
    }
  }, [krigingStep, waterlineLayers]);

  /**
   * @function displayROI
   * @description Draws the acquisition ROI as a highlighted polygon on the map and centers the
   * map on it.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via GoogleMap's onLoad callback.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const displayROI = () => {
    if (!coordinates) return;
    dispatch(Map.centralizeMap(coordinates));
    const formatedCoordinates = aoiPaths(coordinates);
    ROI.current = new window.google.maps.Polygon({
      paths: formatedCoordinates,
      geodesic: true,
      fillColor: 'transparent',
      strokeColor: '#00A391',
      strokeOpacity: 0.6,
      strokeWeight: 5,
      clickable: false,
    });
    ROI.current.setMap(mapRef.current);
    boundsRef.current = new window.google.maps.LatLngBounds();
    formatedCoordinates.flat().forEach((point) => boundsRef.current.extend(point));
    fitBoundsWithOverlayPadding(mapRef.current, boundsRef.current);
  };

  /**
   * Redraws and reframes the area when it arrives after the map has already loaded. A session
   * import sets the coordinates at that point, and without this the area was drawn but never
   * framed, leaving the user looking at the world while the analysis sat somewhere off screen.
   * @see {@link displayROI}
   * @memberof module:Intertidal
   */
  useEffect(() => {
    if (!mapRef.current || !coordinates?.length) return;
    ROI.current?.setMap(null);
    displayROI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates]);

  const steps = [
    {
      selector: '#mapScreen',
      content: t('tour.map.start'),
    },
    {
      selector: '#imageChooserForm',
      content: t('tour.map.imageChooser.info'),
    },
    {
      selector: '#imageChooserSelect',
      content: t('tour.map.imageChooser.select'),
    },
    {
      selector: '#imageChooserLoadButton',
      content: t('tour.map.imageChooser.click'),
    },
  ];

  const [isTourOpen, setIsTourOpen] = useGuidedTour('showMapTour');

  let tideDataTable = JSON.parse(window.localStorage.getItem('tideData')) || [];

  if (Array.isArray(availableDates) && availableDates.length > 0 && tideDataTable.length > 0) {
    const imageTimes = availableDates.map((d) => d.dateTime).filter((t) => typeof t === 'number' && !Number.isNaN(t));
    const parsedTideEntries = tideDataTable
      .map((entry) => ({ entry, t: new Date(entry[0]).getTime() }))
      .filter(({ t }) => !Number.isNaN(t));

    if (imageTimes.length > 0 && parsedTideEntries.length > 0) {
      const NEAREST_PER_IMAGE = 20;
      const sortedByTime = [...parsedTideEntries].sort((a, b) => a.t - b.t);
      const keep = new Set();
      imageTimes.forEach((imageTime) => {
        let lo = 0;
        let hi = sortedByTime.length;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (sortedByTime[mid].t < imageTime) lo = mid + 1;
          else hi = mid;
        }
        let left = lo - 1;
        let right = lo;
        for (let taken = 0; taken < NEAREST_PER_IMAGE && (left >= 0 || right < sortedByTime.length); taken++) {
          const leftDist = left >= 0 ? Math.abs(sortedByTime[left].t - imageTime) : Infinity;
          const rightDist = right < sortedByTime.length ? Math.abs(sortedByTime[right].t - imageTime) : Infinity;
          if (leftDist <= rightDist) {
            keep.add(sortedByTime[left]);
            left--;
          } else {
            keep.add(sortedByTime[right]);
            right++;
          }
        }
      });
      const unparseable = tideDataTable.filter((entry) => Number.isNaN(new Date(entry[0]).getTime()));
      tideDataTable = [...keep].map((item) => item.entry).concat(unparseable);
    }
  }

  let features = [];

  tideDataTable.forEach((entry) => {
    const valorMare = parseFloat(entry[1]);
    const valorArredondado = valorMare.toFixed(2);
    let point = ee.Geometry.MultiPoint([], 'EPSG:4326');
    let f1 = ee.Feature(point, {
      dateTime: entry[0],
      Dado: ee.Number.parse(valorArredondado),
    });
    features.push(f1);
  });

  console.log("Primeiros 5 elementos no array 'tideDataTable':", tideDataTable.slice(0, 5));
  console.log("Primeiros 5 elementos no array 'tideDataTable':", features.slice(0, 5));

  var tideGauge = ee.FeatureCollection(features);

  var timezone = parseInt(window.localStorage.getItem('timezone') || '0', 10);
  console.log('Valor de timezone', timezone);

  tideGauge = tideGauge.map(function (feature) {
    const dateTimeStr = ee.String(feature.get('dateTime'));
    const fusoHorarioNumerico = ee.Number(timezone);
    const dateTime = ee.Date.parse('yyyy-MM-dd HH:mm:ss', dateTimeStr).advance(fusoHorarioNumerico, 'hour');
    return feature.set('dateTime', dateTime);
  });

  /**
   * @function addDatetimeProperty
   * @description Sets a `datetime` property on the image from its `system:time_start`.
   * @param {ee.Image} image - Image to tag.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via the image collection's map, before processing.
   * @memberof module:Intertidal
   * @returns {ee.Image} The image with the `datetime` property set.
   */
  var addDatetimeProperty = function (image) {
    var datetime = ee.Date(image.get('system:time_start'));
    return image.set('datetime', datetime);
  };

  /**
   * @function calculateTimeDifferenceAndSetProperties
   * @description Computes the absolute time difference (in seconds) between an image and a tide
   * gauge feature, tagging the feature with that difference and both dates.
   * @param {ee.Image} image - Image with a `datetime` property.
   * @param {ee.Feature} feature - Tide gauge feature with a `dateTime` property.
   * **Called by:
   * @see {getClosestFeatureImageAndDado}
   * @memberof module:Intertidal
   * @returns {ee.Feature} The feature tagged with `timeDifference`, `dateImage`, and `dateGauge`.
   */
  var calculateTimeDifferenceAndSetProperties = function (image, feature) {
    var imageDate = ee.Date(image.get('datetime'));
    var featureDate = ee.Date(feature.get('dateTime'));
    var diff = featureDate.difference(imageDate, 'second').abs();
    return feature.set({
      timeDifference: diff,
      dateImage: imageDate,
      dateGauge: featureDate,
    });
  };

  /**
   * @function gaussianKernel
   * @description Builds a normalized discrete Gaussian kernel (weights summing to 1) of the given
   * size, mean, and standard deviation.
   * @param {ee.Number} size - Number of samples in the kernel.
   * @param {ee.Number} mean - Mean of the Gaussian curve.
   * @param {ee.Number} sigma - Standard deviation of the Gaussian curve.
   * **Called by:
   * @see {linearGaussianFilter}
   * @memberof module:Intertidal
   * @returns {ee.List} Normalized kernel weights.
   */
  var gaussianKernel = function (size, mean, sigma) {
    var gaussianCurve = function (x, mean, sigma) {
      var divider = ee.Number(sigma).multiply(ee.Number(2).multiply(Math.PI).sqrt());
      var exponent = ee.Number(-1).multiply(
        ee
          .Number(x)
          .subtract(mean)
          .pow(2)
          .divide(ee.Number(2).multiply(ee.Number(sigma).pow(2))),
      );
      return ee.Number(1).divide(divider).multiply(exponent.exp());
    };
    var half = ee.Number(size).divide(2).floor();
    var begin = ee.Number(0).subtract(half),
      end = ee.Number(0).add(half);
    var kernel = ee.List.sequence(begin, end).map(function (i) {
      return gaussianCurve(i, mean, sigma);
    });
    var sum = kernel.reduce(ee.Reducer.sum());
    var normalizedKernel = kernel.map(function (val) {
      return ee.Number(val).divide(sum);
    });
    return normalizedKernel;
  };

  /**
   * @function linearGaussianFilter
   * @description Smooths a line's coordinates with a 3-sample Gaussian filter: slides the kernel
   * along the coordinate list, weighting and summing each windowed longitude/latitude, while
   * keeping the original first and last points fixed. Returns the original coordinates unchanged
   * if the list is shorter than the kernel.
   * @param {ee.List} coordinates - Line coordinates as [lng, lat] pairs.
   * @see {@link gaussianKernel}
   * **Called by:
   * @see {processAllValidFeatures}
   * @memberof module:Intertidal
   * @returns {ee.List} Smoothed coordinates (or the original list, if too short to smooth).
   */
  var linearGaussianFilter = function (coordinates) {
    var samples = 3;
    var mean = 0;
    var sd = 1;
    var coordinateList = ee.List(coordinates);
    var kernelSize = ee.Number(samples);
    var kernelMean = ee.Number(mean);
    var kernelSd = ee.Number(sd);
    var kernel = gaussianKernel(kernelSize, kernelMean, kernelSd);
    var first = coordinateList.reduce(ee.Reducer.first()),
      last = coordinateList.reduce(ee.Reducer.last());
    var sequence = ee.List.sequence(ee.Number(0), coordinateList.length().subtract(kernelSize));
    var path = sequence.map(function (index) {
      var interval = coordinateList.slice(ee.Number(index), ee.Number(index).add(kernelSize)).zip(kernel);
      var gaussian = interval.map(function (element) {
        var asList = ee.List(element);
        var point = ee.List(asList.get(0));
        var weight = ee.Number(asList.get(1));
        return point.map(function (value) {
          return ee.Number(value).multiply(weight);
        });
      });
      var smoothenLong = gaussian
        .map(function (point) {
          return ee.List(point).get(0);
        })
        .reduce(ee.Reducer.sum());
      var smoothenLat = gaussian
        .map(function (point) {
          return ee.List(point).get(1);
        })
        .reduce(ee.Reducer.sum());
      return ee.List([smoothenLong, smoothenLat]);
    });
    var smoothen = ee.List([]).add(first).cat(path).add(last);
    return ee.Algorithms.If(coordinateList.size().lte(kernelSize), coordinateList, smoothen);
  };

  /**
   * @function getClosestFeatureImageAndDado
   * @description Finds the tide gauge feature closest in time to the given waterline image and
   * extracts its tide value and both dates.
   * @param {ee.Image} waterlineImage - Image with a `datetime` property.
   * @param {ee.FeatureCollection} tideGauge - Tide gauge readings to search.
   * @see {@link calculateTimeDifferenceAndSetProperties}
   * **Called by:
   * @see {calculateWaterlineForImage} - via processAllValidFeatures.
   * @memberof module:Intertidal
   * @returns {Object} {closestFeatureImage, tideValue, dateImage, dateGauge}
   */
  function getClosestFeatureImageAndDado(waterlineImage, tideGauge) {
    var closestCollection = tideGauge.map(function (feature) {
      return calculateTimeDifferenceAndSetProperties(waterlineImage, feature);
    });
    var sortedCollection = closestCollection.sort('timeDifference');
    var closestFeatureImage = sortedCollection.first();
    var tideValue = closestFeatureImage.get('Dado');
    var dateImage = closestFeatureImage.get('dateImage');
    var dateGauge = closestFeatureImage.get('dateGauge');
    return {
      closestFeatureImage: closestFeatureImage,
      tideValue: tideValue,
      dateImage: dateImage,
      dateGauge: dateGauge,
    };
  }

  /**
   * @function waterLandFilter
   * @description Masks out all NDWI pixels at or above 0.2 (water surface), keeping only
   * flooding/humidity and drier land ranges. Not currently called anywhere in the codebase.
   * @param {ee.Image} image - NDWI image.
   * @memberof module:Intertidal
   * @returns {ee.Image} The image with water pixels masked out.
   */
  var waterLandFilter = function (image) {
    return image.updateMask(image.lt(0.2));
  };

  /**
   * @function filterPolygons
   * @description Drops polygons with fewer vertices than the mission-appropriate minimum. Not
   * currently called anywhere in the codebase.
   * @param {ee.List} polygon - A polygon's coordinate rings.
   * @see {@link getMinVerticesThreshold}
   * @memberof module:Intertidal
   * @returns {ee.List|null} The polygon, or null if it has too few vertices.
   */
  var filterPolygons = function (polygon) {
    var numVertices = ee.List(ee.List(polygon).get(0)).length();
    var minVertices = getMinVerticesThreshold();
    return ee.Algorithms.If(numVertices.gt(minVertices), polygon, null);
  };

  /**
   * @function getPolygonWithMostVertices
   * @description Returns the feature with the most vertices from a collection. Not currently
   * called anywhere in the codebase.
   * @param {ee.FeatureCollection} featureCollection - Features tagged with a `numVertices` property.
   * @memberof module:Intertidal
   * @returns {ee.Feature} The feature with the highest `numVertices`, or a null feature if empty.
   */
  var getPolygonWithMostVertices = function (featureCollection) {
    var size = featureCollection.size();
    var result = ee.Algorithms.If(size.gt(0), featureCollection.sort('numVertices', false).first(), null);
    return ee.Feature(result);
  };

  let selectedPolyline = null;
  let selectedInfoWindow = null;
  /**
   * @function plotEvaluatedSync
   * @description Draws each LineString feature as an interactive Google Maps polyline: clicking a
   * line (when not in Kriging removal mode) highlights it and opens an InfoWindow listing its
   * properties.
   * @param {google.maps.Map} map - Map to draw on.
   * @param {Object} evaluatedData - GeoJSON FeatureCollection of LineString features.
   * @param {String} colorForLines - Stroke color applied to every line.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via startWaterlineRun, printWaterlines, and fetchAndPlotSingleWaterline.
   * @memberof module:Intertidal
   * @returns {Array<google.maps.Polyline>} The created polylines.
   */
  function plotEvaluatedSync(map, evaluatedData, colorForLines) {
    if (!map) {
      console.error('Mapa não disponível.');
      return [];
    }
    if (!evaluatedData || !evaluatedData.features) {
      console.error('Dados avaliados inválidos.');
      return [];
    }

    const createdPolylines = [];
    let dateTime = null;
    let tideValue = null;

    evaluatedData.features.forEach((feature) => {
      const geometry = feature.geometry;
      if (geometry.type === 'LineString') {
        const path = geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));

        const color = colorForLines;
        if (feature.properties) {
          dateTime = feature.properties.dateTime || dateTime;
          tideValue = feature.properties.tideValue || tideValue;
        }

        const polyline = new window.google.maps.Polyline({
          path,
          map: map,
          strokeColor: color,
          strokeOpacity: 1.0,
          strokeWeight: 2,
        });

        polyline.originalColor = color;
        createdPolylines.push(polyline);

        polyline.addListener('click', (e) => {
          if (isRemovalModeRef.current) return;
          if (selectedPolyline) {
            selectedPolyline.setOptions({
              strokeWeight: 2,
              strokeColor: selectedPolyline.originalColor,
            });
          }
          if (selectedInfoWindow) {
            selectedInfoWindow.close();
          }

          polyline.setOptions({
            strokeWeight: 5,
            strokeColor: '#FF00FF',
          });
          selectedPolyline = polyline;

          const content =
            Object.entries(feature.properties || {})
              .map(([key, val]) => {
                if (key === 'tideValue' && typeof val === 'number') {
                  val = val.toFixed(2);
                }
                const label = t(`intertidal.attributes.${key}`, key);
                return `<strong>${label}:</strong> ${val}`;
              })
              .join('<br>') || t('forms.intertidal.attributes.noProperties');
          selectedInfoWindow = new window.google.maps.InfoWindow({
            content,
            position: e.latLng,
          });
          selectedInfoWindow.open(map);
        });
      }
    });

    console.log('Waterlines plotadas com interatividade.');
    return { polylines: createdPolylines, dateTime, tideValue };
  }

  /**
   * @function drawAweiLayers
   * @description Draws each cached AWEI layer as a GroundOverlay on the map, preferring a
   * layer-specific bounds (set by VectorEditor after a re-clip) over the AOI bounds.
   * @param {google.maps.Map} map - Map instance to draw on.
   * @param {Array<Object>} layerURLs - Array of {url, date, bounds?} AWEI layer descriptors.
   * @param {google.maps.LatLngBounds} bounds - Fallback AOI bounds.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via the main processing effect, when cached AWEI layers exist.
   * @memberof module:Intertidal
   * @returns {Array<google.maps.GroundOverlay>} The created overlays.
   */
  const drawAweiLayers = (map, layerURLs, bounds) => {
    if (!map || !layerURLs || layerURLs.length === 0) {
      console.warn('Mapa ou URLs de layers AWEI não disponíveis.');
      return [];
    }
    const createdOverlays = [];

    layerURLs.forEach((layerInfo, index) => {
      const layerBounds = layerInfo.bounds || bounds;

      const overlayAWEI = new window.google.maps.GroundOverlay(layerInfo.url, layerBounds);
      overlayAWEI.setMap(map);
      createdOverlays.push(overlayAWEI);
    });
    return createdOverlays;
  };

  /**
   * @function printWaterlines
   * @description Evaluates the accumulated `evaluated` FeatureCollection, sanitizes it into
   * LineStrings, and plots it on the map. Not currently called anywhere in the codebase.
   * @see {@link convertGeoJSONToLineStrings}
   * @see {@link prepareGeoJsonForGoogleMaps}
   * @see {@link plotEvaluatedSync}
   * @memberof module:Intertidal
   * @returns {Promise<void>}
   */
  const printWaterlines = async () => {
    console.log('Printing waterlines....');
    try {
      console.log('executing getInfo for evaluated variable...');
      evaluatedEE = evaluated;
      evaluatedEE = await evaluatedEE.getInfo();
      console.log('Antes da sanitização', evaluatedEE);

      console.log('CONTEÚDO BRUTO (para geojson.io):', JSON.stringify(evaluatedEE, null, 2));

      console.group('🔍 DEBUG DETALHADO DO FEATURECOLLECTION');
      console.log('Total de features:', evaluatedEE?.features?.length || 0);

      evaluatedEE?.features?.forEach((feature, index) => {
        console.group(`Feature ${index + 1}:`);
        console.log('Tipo de geometria:', feature.geometry?.type);
        console.log('Número de coordenadas:', feature.geometry?.coordinates?.length || 0);

        if (feature.geometry?.coordinates?.length > 0) {
          console.log(
            'Primeiras 3 coordenadas:',
            feature.geometry.coordinates
              .slice(0, 3)
              .map((coord) => `[${coord[0].toFixed(6)}, ${coord[1].toFixed(6)}]`)
              .join(', '),
          );
        }

        console.log('Properties:', feature.properties);
        if (feature.properties) {
          Object.entries(feature.properties).forEach(([key, value]) => {
            console.log(`  - ${key}:`, value);
          });
        }
        console.groupEnd();
      });
      console.groupEnd();

      const sanitizedEvaluated = convertGeoJSONToLineStrings(evaluatedEE);
      console.log('Depois da sanitização', sanitizedEvaluated);

      console.group('🔍 DEBUG APÓS SANITIZAÇÃO');
      console.log('Total de features sanitizadas:', sanitizedEvaluated?.features?.length || 0);
      sanitizedEvaluated?.features?.forEach((feature, index) => {
        console.log(`Feature ${index + 1}:`, {
          tipo: feature.geometry?.type,
          numCoordenadas: feature.geometry?.coordinates?.length,
          properties: feature.properties,
        });
      });
      console.groupEnd();

      const geoJsonReady = prepareGeoJsonForGoogleMaps(sanitizedEvaluated);

      console.log('DEBUG FINAL - GeoJSON enviado para o mapa:', JSON.stringify(geoJsonReady, null, 2));

      console.log('Depois de geoJsonReady', geoJsonReady);
      if (geoJsonReady) {
        plotEvaluatedSync(map, sanitizedEvaluated);
      } else {
        console.error('Não foi possível preparar o GeoJSON.');
      }
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * @function fetchAndPlotSingleWaterline
   * @description Evaluates a single waterline FeatureCollection, sanitizes it, plots it on the
   * map, and accumulates it into `evaluatedEE` for later export. Not currently called anywhere
   * in the codebase.
   * @param {ee.FeatureCollection} waterlineEE - Waterline features for a single image.
   * @see {@link convertGeoJSONToLineStrings}
   * @see {@link prepareGeoJsonForGoogleMaps}
   * @see {@link plotEvaluatedSync}
   * @memberof module:Intertidal
   * @returns {Promise<void>}
   */
  const fetchAndPlotSingleWaterline = async (waterlineEE) => {
    try {
      const waterlineGeoJSON = await waterlineEE.getInfo();

      if (!waterlineGeoJSON || waterlineGeoJSON.features.length === 0) {
        console.log("Nenhuma linha d'água válida retornada para esta imagem.");
        return;
      }

      const sanitized = convertGeoJSONToLineStrings(waterlineGeoJSON);
      const geoJsonReady = prepareGeoJsonForGoogleMaps(sanitized);

      if (geoJsonReady) {
        plotEvaluatedSync(map, sanitized);

        if (!evaluatedEE) {
          evaluatedEE = { type: 'FeatureCollection', features: [] };
        }
        evaluatedEE.features.push(...sanitized.features);
      }
    } catch (e) {
      console.error("Erro ao buscar e plotar linha d'água individual:", e);
    }
  };

  /**
   * @function handleStep1Next
   * @description Hides every polyline marked for removal in Kriging step 1, then advances to step 2.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via KrigingPanel's onNext prop.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const handleStep1Next = () => {
    waterlineLayers.forEach((layer, layerIdx) => {
      layer.polylines.forEach((polyline, polyIdx) => {
        const key = `${layerIdx}-${polyIdx}`;
        if (removedPolylineIndices.has(key)) {
          polyline.setMap(null);
        }
      });
    });
    setKrigingStep(2);
  };

  /**
   * @function handleKrigingCancel
   * @description Restores every waterline polyline's original style/visibility and resets the
   * Kriging preparation wizard state.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via KrigingPanel's onCancel prop.
   * @see {handleKrigingConfirm}
   * @memberof module:Intertidal
   * @returns {void}
   */
  const handleKrigingCancel = () => {
    waterlineLayers.forEach((layer) => {
      layer.polylines.forEach((polyline) => {
        polyline.setOptions({
          strokeOpacity: 1.0,
          strokeColor: polyline.originalColor,
          strokeWeight: 2,
          icons: [],
        });
        if (layer.visible) polyline.setMap(map);
      });
    });
    setKrigingStep(null);
    setRemovedPolylineIndices(new Set());
    setSelectedDates(new Set());
  };

  /**
   * @function handleKrigingConfirm
   * @description Builds the final set of waterline feature indices to exclude from the Kriging
   * interpolation — individually removed polylines plus entire unselected date groups — then
   * resets the wizard and kicks off the interpolation with the filtered features.
   * @see {@link handleKrigingCancel}
   * @see {@link handleKrigingInterpolation}
   * **Called by:
   * @see {ProcessingIntertidalPage} - via KrigingPanel's onConfirm prop.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const handleKrigingConfirm = () => {
    const featureIndicesToRemove = new Set();

    removedPolylineIndices.forEach((key) => {
      const [layerIdx, polyIdx] = key.split('-').map(Number);
      const layer = waterlineLayers[layerIdx];
      if (layer) featureIndicesToRemove.add(layer.featureStartIndex + polyIdx);
    });

    waterlineLayers.forEach((layer, idx) => {
      if (!selectedDates.has(idx)) {
        for (let i = 0; i < layer.featureCount; i++) {
          featureIndicesToRemove.add(layer.featureStartIndex + i);
        }
      }
    });

    const filteredFeatures = evaluatedEE.features.filter((_, i) => !featureIndicesToRemove.has(i));

    handleKrigingCancel();
    handleKrigingInterpolation(filteredFeatures);
  };

  /**
   * @function toggleWaterlineVisibility
   * @description Toggles a waterline layer's visibility, hiding/showing all of its polylines on
   * the map.
   * @param {Number} index - Index of the layer within `waterlineLayers`.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via each waterline layer's visibility icon.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const toggleWaterlineVisibility = (index) => {
    setWaterlineLayers((prev) => {
      const updated = [...prev];
      updated[index].visible = !updated[index].visible;

      updated[index].polylines.forEach((polyline) => {
        polyline.setMap(updated[index].visible ? map : null);
      });

      return updated;
    });
  };

  /**
   * @function toggleAweiVisibility
   * @description Toggles the visibility of every AWEI GroundOverlay on the map.
   * @param {boolean} [visibility] - Desired visibility; toggles the current state if omitted.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via the AWEI layer's visibility control.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const toggleAweiVisibility = (visibility) => {
    const newVisibility = visibility !== undefined ? visibility : !aweiVisible;
    setAweiVisible(newVisibility);

    aweiOverlays.forEach((overlay) => {
      overlay.setMap(newVisibility ? map : null);
    });
  };

  /**
   * @function toggleKrigingVisibility
   * @description Toggles the visibility of the Kriging interpolation GroundOverlay.
   * @param {boolean} [visibility] - Desired visibility; toggles the current state if omitted.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via the Kriging layer's visibility control.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const toggleKrigingVisibility = (visibility) => {
    const newVisibility = visibility !== undefined ? visibility : !krigingVisible;
    setKrigingVisible(newVisibility);

    if (krigingOverlay) {
      krigingOverlay.setMap(newVisibility ? map : null);
    }
  };

  /**
   * @function displayKrigingOverlay
   * @description Replaces the current Kriging GroundOverlay with a new one built from the given
   * thumbnail URL, fit to the given coordinates' bounds.
   * @param {String} thumbUrl - Thumbnail URL of the interpolated tide surface.
   * @param {Array<Array<Number>>} coordinatesArray - Polygon coordinates defining the overlay bounds.
   * **Called by:
   * @see {handleKrigingInterpolation}
   * @memberof module:Intertidal
   * @returns {void}
   */
  const displayKrigingOverlay = (thumbUrl, coordinatesArray) => {
    if (!map) return;

    const formatedCoordinates = coordinatesArray.map((coord) => ({
      lat: coord[1],
      lng: coord[0],
    }));
    const bounds = new window.google.maps.LatLngBounds();
    formatedCoordinates.forEach((coord) => bounds.extend(coord));

    if (krigingOverlay) {
      krigingOverlay.setMap(null);
    }

    const overlay = new window.google.maps.GroundOverlay(thumbUrl, bounds);
    overlay.setMap(map);
    overlay.setOpacity(0.7);

    setKrigingOverlay(overlay);
  };

  /**
   * @function handleKrigingInterpolation
   * @description Runs the Kriging tide-surface interpolation and displays it as a map overlay.
   * 1. Builds the AWEI mask and the waterline FeatureCollection to interpolate (either the given
   *    override, filtered by handleKrigingConfirm, or all accumulated waterlines).
   * 2. Runs the interpolation, then derives the visualization/legend range from the actual
   *    interpolated raster's min/max (via `calculateKrigingStatistics`, `reduceRegion` over the
   *    `interpolated_tide` band) rather than the input waterlines' tide values — Kriging can
   *    over/undershoot those input values, so the colorbar needs to reflect what was actually
   *    generated. A 10% margin is added on top for the color palette's own range.
   * 3. Generates preview/download thumbnails, displays the overlay, and stores the export data.
   * @param {Array<Object>} [featuresOverride=null] - Waterline features to use instead of all accumulated ones.
   * @see {@link performKrigingInterpolation}
   * @see {@link createAweiMask}
   * @see {calculateKrigingStatistics}
   * @see {@link displayKrigingOverlay}
   * **Called by:
   * @see {handleKrigingConfirm}
   * @memberof module:Intertidal
   * @returns {Promise<void>}
   */
  const handleKrigingInterpolation = async (featuresOverride = null) => {
    setIsKrigingProcessing(true);

    try {
      const { performKrigingInterpolation, createAweiMask, calculateKrigingStatistics } = await import(
        '../../../algorithms/intertidal/kriging'
      );

      const aweiMask = createAweiMask();
      if (!aweiMask) {
        console.error('Máscara da zona intermaré não disponível');
        dispatch(Snack.error('intertidal-kriging-mask-unavailable', t('forms.intertidal.krigingMaskUnavailable')));
        setIsKrigingProcessing(false);
        return;
      }

      const featuresToUse = featuresOverride || evaluatedEE?.features;

      if (!featuresToUse || featuresToUse.length === 0) {
        console.error('Nenhuma waterline disponível para interpolação');
        dispatch(Snack.error('intertidal-kriging-no-waterlines', t('forms.intertidal.krigingNoWaterlines')));
        setIsKrigingProcessing(false);
        return;
      }

      const waterlineFC = ee.FeatureCollection(featuresToUse);

      const coordinatesArray = JSON.parse(window.localStorage.getItem('AOIIntertidal'));
      if (!coordinatesArray) {
        console.error('AOI não encontrada');
        dispatch(Snack.error('intertidal-kriging-aoi-not-found', t('forms.intertidal.krigingAoiNotFound')));
        setIsKrigingProcessing(false);
        return;
      }
      const geometry = aoiGeometry(coordinatesArray);

      const krigingImage = performKrigingInterpolation(waterlineFC, geometry, aweiMask);

      const tideValues = featuresToUse.map((f) => f.properties?.tideValue).filter((v) => v !== undefined && v !== null);

      if (tideValues.length === 0) {
        console.error('Nenhum valor de maré encontrado nas waterlines');
        dispatch(Snack.error('intertidal-kriging-no-tide-values', t('forms.intertidal.krigingNoTideValues')));
        setIsKrigingProcessing(false);
        return;
      }
      console.log('Valores de maré encontrados:', tideValues);

      const { min, max } = await calculateKrigingStatistics(krigingImage, geometry);

      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        console.error('Estatísticas do raster Kriging vazias/inválidas (min/max não finitos):', { min, max });
        dispatch(Snack.error('intertidal-kriging-empty-mask', t('forms.intertidal.krigingEmptyMaskError')));
        setIsKrigingProcessing(false);
        return;
      }

      const margin = (max - min) * 0.1;
      const minVis = min - margin;
      const maxVis = max + margin;

      const visParams = {
        region: geometry,
        dimensions: 256,
        bands: ['interpolated_tide'],
        min: minVis,
        max: maxVis,
        palette: ['blue', 'cyan', 'yellow', 'orange', 'red'],
        format: 'png',
      };

      try {
        const thumbUrl = krigingImage.getThumbURL(visParams);
        const downloadParams = { ...visParams, dimensions: 512 };
        const downloadUrl = krigingImage.getThumbURL(downloadParams);

        displayKrigingOverlay(thumbUrl, coordinatesArray);
        setKrigingMinValue(minVis);
        setKrigingMaxValue(maxVis);
        setKrigingRealMinValue(min);
        setKrigingRealMaxValue(max);
        setKrigingExportData({ image: krigingImage, geometry });
        setIsKrigingProcessing(false);
      } catch (urlError) {
        console.error('Erro ao gerar URLs:', urlError);
        dispatch(Snack.error('intertidal-kriging-visualization-error', t('forms.intertidal.krigingVisualizationError')));
        setIsKrigingProcessing(false);
      }
    } catch (error) {
      console.error('Erro na interpolação kriging:', error);
      dispatch(Snack.error('intertidal-kriging-generic-error', t('forms.intertidal.krigingGenericError')));
      setIsKrigingProcessing(false);
    }
  };

  /**
   * @function exportKrigingTiff
   * @description Requests a GeoTIFF download URL for the interpolated Kriging surface and
   * triggers its download.
   * @see {@link triggerDownload}
   * **Called by:
   * @see {ProcessingIntertidalPage} - via the "Export GeoTIFF" button's onClick.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const exportKrigingTiff = () => {
    if (!krigingExportData) {
      dispatch(Snack.error('intertidal-kriging-export-missing-data', t('forms.intertidal.krigingExportMissingData')));
      return;
    }

    const { image, geometry } = krigingExportData;
    setIsExportingKrigingTiff(true);

    /**
     * @function triggerDownload
     * @description Creates a temporary anchor element to trigger a browser download of the
     * given URL as "kriging_interpolation.tif".
     * @param {String} url - GeoTIFF download URL.
     * **Called by:
     * @see {exportKrigingTiff}
     * @memberof module:Intertidal
     * @returns {void}
     */
    const triggerDownload = (url) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = 'kriging_interpolation.tif';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    try {
      image.getDownloadURL(
        {
          name: 'kriging_interpolation',
          region: geometry,
          scale: getScaleForMission(),
          crs: 'EPSG:4326',
          format: 'GEO_TIFF',
        },
        (url, error) => {
          setIsExportingKrigingTiff(false);
          if (error || !url) {
            console.error('Erro ao gerar GeoTIFF:', error);
            dispatch(
              Snack.error('intertidal-kriging-geotiff-generation-error', t('forms.intertidal.krigingGeotiffGenerationError')),
            );
            return;
          }
          triggerDownload(url);
        },
      );
    } catch (e) {
      setIsExportingKrigingTiff(false);
      console.error('Erro ao exportar GeoTIFF:', e);
      dispatch(Snack.error('intertidal-kriging-geotiff-export-error', t('forms.intertidal.krigingGeotiffExportError')));
    }
  };

  /**
   * @function exportSessionState
   * @description Downloads a JSON snapshot of the current analysis's configuration (satellite,
   * AOI, tide/period data) and its already-evaluated waterlines, for Intertidal's "import session"
   * icon to replay later — a developer/testing shortcut so QA doesn't have to redo the whole
   * wizard every time they need a completed result to test against. The AWEI/Kriging pipeline
   * isn't captured (see sessionExport.js's own doc comment) — only the waterlines themselves.
   * @see {@link buildSessionExport}
   * **Called by:
   * @see {NavigationBar} - via the standardized "export session for testing" icon's onClick.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const exportSessionState = () => {
    exportSession(
      buildSessionExport({ coordinates, availableDates, waterlineLayers, evaluatedEE }),
      kmlFileName,
    );
  };
  useRegisterSessionExport('intertidal', waterlineLayers.length > 0 ? exportSessionState : null);

  /**
   * @function exportGeoJsonLayers
   * @description Writes the traced waterlines, each with the tide level it was observed at, plus the area of
   * interest, to a GeoJSON file for a GIS.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const exportGeoJsonLayers = () => {
    if (!exportGeoJson({ coordinates, evaluatedEE, waterlineLayers })) notifyError(t('forms.acquisition.geoJsonExport.empty'));
  };
  useRegisterGeoJsonExport('intertidal', waterlineLayers.length > 0 ? exportGeoJsonLayers : null);

  /**
   * @function reportText
   * @description Every piece of wording the report needs, translated here and handed to the
   * builder, which stays a pure function of the analysis - see {@link buildIntertidalReport}.
   * @memberof module:Core
   * @returns {Object}
   */
  const reportText = () => {
    const key = (name) => `forms.core.pdfReport.${name}`;
    const own = (name) => `forms.core.pdfReport.intertidal.${name}`;
    const group = (name) => t(own(name), { returnObjects: true, defaultValue: t(key(name), { returnObjects: true }) });
    return {
      ...t(key('common'), { returnObjects: true }),
      ...t(own('text'), { returnObjects: true }),
      subtitle: t(key('subtitle'), { date: new Date().toLocaleString() }),
      missingImage: (caption) => t(key('missingImage'), { caption }),
      screenCaption: t(key('screenCaption')),
      rates: group('rates'),
      classes: group('classes'),
      transects: group('transects'),
      transectNote: {
        complete: (values) => t(key('transectComplete'), values),
        truncated: (values) => t(key('transectTruncated'), values),
      },
      transectComplete: (values) => t(key('transectComplete'), values),
      transectTruncated: (values) => t(key('transectTruncated'), values),
      forecastYears: (values) => t(key('forecastYears'), values),
      missingTideNote: (values) => t(own('missingTideNote'), values),
      tide: {
        range: (values) => t(own('tideRangeValue'), values),
        span: (values) => t(own('tideSpanValue'), values),
      },
      errors: group('errors'),
      byDate: group('byDate'),
      byTransect: group('byTransect'),
      bays: group('bays'),
      controlPoints: group('controlPoints'),
      waterlines: group('waterlines'),
      cover: group('cover'),
    };
  };

  /**
   * @function exportPdfReport
   * @description Writes the whole analysis to one PDF.
   * The map is captured from the screen rather than fetched, because what this module draws is a
   * Google Maps overlay and not an image with a URL - see {@link captureImageForReport}.
   * @memberof module:Core
   * @returns {Promise<void>}
   */
  const exportPdfReport = async () => {
    try {
      const text = reportText();
      /**
       * The map is captured from the screen, so it is framed on the analysis first - otherwise the
       * report shows wherever the user had last panned to. See {@link settleMapForCapture}, which
       * also waits for the tiles of the new view to arrive.
       */
      await settleMapForCapture(mapRef.current, () => fitCoordinatesWithOverlayPadding(mapRef.current, coordinates));
      const screen = await captureImageForReport(APP_ROOT_SELECTOR, text.screenCaption);
      const report = buildIntertidalReport({
        coordinates,
        waterlineLayers,
        images: [screen],
        text,
      });
      renderReport(report).save(reportFileName(REPORT_MODULE));
    } catch (error) {
      console.error('exportPdfReport error:', error);
      notifyError(t('forms.core.pdfReport.failed'));
    }
  };
  useRegisterPdfExport(REPORT_MODULE, waterlineLayers.length > 0 ? exportPdfReport : null);


  /**
   * @function tideSeriesForChart
   * @description The full recorded tide series (`localStorage.tideData`), read directly rather
   * than reusing the in-memory `tideDataTable` above — that one gets pruned to only the readings
   * near each image's acquisition time (see the payload-size comment further up), so it can't
   * stand in for a "full series" background line the way `TideChartModal` needs.
   * **Called by:
   * @see {ProcessingIntertidalPage} - via TideChartModal's tideSeries prop.
   * @memberof module:Intertidal
   * @returns {Array<{date: string, level: number}>}
   */
  const tideSeriesForChart = useMemo(() => {
    let raw = [];
    try {
      raw = JSON.parse(window.localStorage.getItem('tideData')) || [];
    } catch {
      raw = [];
    }
    return raw
      .map(([date, level]) => ({ date, level: Number(level) }))
      .filter((entry) => Number.isFinite(entry.level));
  }, []);

  const tideChartAcquisitionPoints = useMemo(
    () => waterlineLayers.map((layer) => ({ date: layer.date, tideLevel: layer.tideValue })),
    [waterlineLayers],
  );

  return (
    <ThemeProvider theme={intertidalTheme}>
    <Box className={classes.wrapper}>
      {isLoading && <ActivityIndicator forceShow={true} title={t('forms.core.loading.waterlines')} />}
      <Grow in={true} unmountOnExit>
        <Grid container justifyContent="center" spacing={0} id="mapScreen" className={classes.mapOverlay}>
          <Grid
            item
            xs={9}
            className={classes.mapContainer}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: 8,
              flexBasis: 'auto',
              flexGrow: 0,
              width: OVERLAY_MAIN_COLUMN_WIDTH,
              maxWidth: OVERLAY_MAIN_COLUMN_WIDTH,
              paddingTop: OVERLAY_COLUMN_PADDING_TOP,
              paddingLeft: OVERLAY_SIDE_GUTTER,
              paddingRight: OVERLAY_SCROLLBAR_GUTTER,
              height: '100%',
              overflowY: 'auto',
              maxHeight: OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
            }}>
            <OverlayPanelsToggle side="left" />
            <Paper className={classes.panelWrapper} elevation={1}>
              <Box className={classes.panelHeader}>
                <Box display="flex" alignItems="center" gridGap={6}>
                  <LayersOutlined fontSize="small" color="primary" />
                  <Typography variant="h6">{t('forms.shorelineAnalysis.layersPanelTitle')}</Typography>
                </Box>
                <IconButton size="small" onClick={() => setLayersPanelOpen((prev) => !prev)}>
                  {layersPanelOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={layersPanelOpen}>
                <ShapeList
                  embedded
                  roiCoords={ROI}
                  customLayers={customLayers}
                  onCustomLayerToggle={(layerId, newVisibility) => {
                    if (layerId === 'awei-raster') {
                      toggleAweiVisibility(newVisibility);
                    } else if (layerId === 'kriging-interpolation') {
                      toggleKrigingVisibility(newVisibility);
                    } else if (layerId.startsWith('waterline-')) {
                      const index = parseInt(layerId.split('-')[1]);
                      toggleWaterlineVisibility(index);
                    }
                  }}
                />
              </Collapse>
            </Paper>

            {krigingOverlay && krigingVisible && krigingRealMinValue !== null && krigingRealMaxValue !== null && (
              <Card
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  right: '20px',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                  minWidth: '200px',
                }}>
                <CardContent style={{ padding: '0' }}>
                  <Typography
                    variant="subtitle2"
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      textAlign: 'center',
                    }}>
                    {t('forms.intertidal.krigingLegend') || 'Nível de Maré (m)'}
                  </Typography>

                  <Box
                    style={{
                      height: '20px',
                      background: 'linear-gradient(to right, blue, cyan, yellow, orange, red)',
                      border: '1px solid #666',
                      borderRadius: '4px',
                      marginBottom: '6px',
                    }}
                  />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {krigingRealMinValue.toFixed(2)}m
                    </Typography>
                    <Typography variant="caption" style={{ color: '#666' }}>
                      {((krigingRealMinValue + krigingRealMaxValue) / 2).toFixed(2)}m
                    </Typography>
                    <Typography variant="caption" style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                      {krigingRealMaxValue.toFixed(2)}m
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            <ImageChooserCard intertidalMode vertical="intertidal" />
            <div>
              {zoneOnlyMode && (
                <Card style={{ width: '100%', backgroundColor: 'rgba(25, 118, 210, 0.06)' }}>
                  <CardContent style={{ padding: 10 }}>
                    <Typography variant="body2">{t('forms.intertidal.zoneOnlyResultNotice')}</Typography>
                  </CardContent>
                </Card>
              )}
              <Card style={{ width: '100%' }}>
                <CardContent className={classes.item} style={{ display: 'flex', flexDirection: 'row', padding: 10 }}>
                  {!zoneOnlyMode && (
                    <Button
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        width: '100%',
                      }}
                      onClick={() => WaterLinesExport.exportShapeFile()}>
                      {' '}
                      <Avatar>
                        <Waves></Waves>
                      </Avatar>{' '}
                      <p>{t('forms.intertidal.exportData')}</p>{' '}
                    </Button>
                  )}
                  {tideSeriesForChart.length > 0 && (
                    <Button
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        width: '100%',
                      }}
                      onClick={() => setTideChartOpen(true)}>
                      <Avatar>
                        <ShowChart />
                      </Avatar>
                      <p>{t('forms.intertidal.viewTideChart')}</p>
                    </Button>
                  )}
                  {krigingExportData && (
                    <Button
                      onClick={exportKrigingTiff}
                      disabled={isExportingKrigingTiff}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        width: '100%',
                      }}>
                      <Avatar>
                        <GetApp />
                      </Avatar>
                      <p>
                        {isExportingKrigingTiff
                          ? t('forms.intertidal.generatingTiff') || 'Gerando GeoTIFF...'
                          : t('forms.intertidal.exportKrigingTiff') || 'Exportar Kriging (.tiff)'}
                      </p>
                    </Button>
                  )}
                </CardContent>
              </Card>

              <TideChartModal
                open={tideChartOpen}
                onClose={() => setTideChartOpen(false)}
                tideSeries={tideSeriesForChart}
                acquisitionPoints={tideChartAcquisitionPoints}
              />

              {!isProcessing && waterlineLayers.length > 0 && (
                <Card style={{ width: '100%' }}>
                  <CardContent
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: 10,
                    }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setWaterlineLayers((prev) =>
                          prev.map((layer) => {
                            if (!layer.visible) {
                              layer.polylines.forEach((p) => p.setMap(map));
                            }
                            return { ...layer, visible: true };
                          }),
                        );
                        setKrigingStep(1);
                        setRemovedPolylineIndices(new Set());
                        setSelectedDates(new Set(waterlineLayers.map((_, i) => i)));
                      }}
                      disabled={isKrigingProcessing || krigingStep !== null}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        width: '100%',
                        marginTop: 8,
                      }}>
                      <p>{t('forms.intertidal.generateKriging')}</p>
                    </Button>

                    {isKrigingProcessing && (
                      <Box display="flex" alignItems="center" p={1} mt={1}>
                        <CircularProgress size={20} style={{ marginRight: 8 }} />
                        <Typography variant="body2">{t('forms.intertidal.processingKriging')}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {isProcessing && (
                <Box
                  display="flex"
                  alignItems="center"
                  p={1}
                  bgcolor="background.paper"
                  borderRadius={4}
                  boxShadow={3}
                  style={{ width: '100%' }}>
                  <CircularProgress size={24} style={{ marginRight: '10px' }} />
                  <Typography variant="body2">
                    {t('forms.intertidal.waterlineProgress', { completed: currentImage, total: totalImages })}
                  </Typography>
                </Box>
              )}
            </div>
          </Grid>
          <Grid
            item
            xs={3}
            style={{
              display: 'flex',
              flexDirection: 'column',
              ...OVERLAY_COLUMN_LAYOUT,
              gap: 8,
              flexBasis: 'auto',
              flexGrow: 0,
              width: OVERLAY_SIDE_COLUMN_WIDTH,
              maxWidth: OVERLAY_SIDE_COLUMN_WIDTH,
              marginLeft: 'auto',
              paddingTop: OVERLAY_COLUMN_PADDING_TOP,
              paddingLeft: OVERLAY_SCROLLBAR_GUTTER,
              paddingRight: OVERLAY_SIDE_GUTTER,
              overflowY: 'auto',
              maxHeight: OVERLAY_COLUMN_MAX_HEIGHT,
            }}>
            <OverlayPanelsToggle side="right" />
            <BackgroundMapPanel
              mapType={mapType}
              onMapTypeChange={handleMapTypeChange}
              labelsVisible={labelsVisible}
              onLabelsVisibleChange={setLabelsVisible}
              t={t}
            />
            {krigingStep !== null ? (
              <KrigingPanel
                step={krigingStep}
                waterlineLayers={waterlineLayers}
                removedCount={removedPolylineIndices.size}
                selectedDates={selectedDates}
                onToggleDate={(idx) => {
                  setSelectedDates((prev) => {
                    const next = new Set(prev);
                    const willBeSelected = !next.has(idx);
                    willBeSelected ? next.add(idx) : next.delete(idx);

                    const layer = waterlineLayers[idx];
                    if (layer) {
                      layer.polylines.forEach((p) => {
                        p.setMap(willBeSelected ? map : null);
                      });
                    }

                    return next;
                  });
                }}
                onResetRemovals={() => {
                  waterlineLayers.forEach((layer) => {
                    layer.polylines.forEach((p) => {
                      p.setOptions({
                        strokeOpacity: 1.0,
                        strokeColor: p.originalColor,
                        strokeWeight: 2,
                        icons: [],
                      });
                    });
                  });
                  setRemovedPolylineIndices(new Set());
                }}
                onNext={handleStep1Next}
                onBack={() => setKrigingStep(1)}
                onCancel={handleKrigingCancel}
                onConfirm={handleKrigingConfirm}
              />
            ) : (
              <LoadedImagesAccordion />
            )}
          </Grid>
        </Grid>
      </Grow>
      <ReturnButtons lastStepPath="/main/intertidal/5" />
      <GoogleMap
        style={{ width: '100%', height: 'calc(100vh - 48px)' }}
        mapOptions={{ mapTypeControl: false }}
        onLoad={(createdMap) => {
          mapRef.current = createdMap;
          createdMap.setMapTypeId(mapType);
          setMap(createdMap);
          setMapReady(true);
          displayROI(createdMap);
        }}
      />
      <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
    </Box>
    </ThemeProvider>
  );
};

export default ProcessingIntertidalPage;
